import { useMemo } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { F0Form, f0FormField, useF0Form } from '@factorialco/f0-react'
import {
  AtsCreateApplicationPhaseDocument,
  AtsGetHiringPipelinePhasesDocument,
} from 'generated/resources/graphql'
import { maxBy } from 'lodash'
import { z } from 'zod'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { UNASSIGNED_STAGE, getPhaseType } from 'modules/ats/lib/utils/hiringStages'
import { HiringStageWithApplicationPhases } from 'modules/ats/pages/JobPostingSettings/HiringPipeline/utils'

interface Props {
  jobId: string
  hiringStage: HiringStageWithApplicationPhases
  onClose: () => void
}

export const AddApplicationPhaseModal = ({ onClose, jobId, hiringStage }: Props) => {
  const [createAtsApplicationPhase, { loading }] = useMutation(AtsCreateApplicationPhaseDocument, {
    extractorFn: (data) => data.atsMutations.createAtsApplicationPhase,
    refetchQueries: [AtsGetHiringPipelinePhasesDocument],
    awaitRefetchQueries: true,
  })

  const { formRef, submit, isSubmitting } = useF0Form()

  const schema = useMemo(
    () =>
      z.object({
        name: f0FormField(
          z
            .string()
            .min(
              1,
              i18n.t('ats.job_posting_settings.hiring_pipeline.add_phase_modal.phase_input_error')
            ),
          {
            label: i18n.t('ats.job_posting_settings.hiring_pipeline.phase_input_label'),
            fieldType: 'text',
          }
        ),
      }),
    []
  )

  return (
    <Modal
      title={i18n.t('ats.job_posting_settings.hiring_pipeline.add_phase_modal.title')}
      description={i18n.t('ats.job_posting_settings.hiring_pipeline.add_phase_modal.description')}
      illustration="automations"
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" onClick={() => submit()} loading={loading || isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <F0Form
        formRef={formRef}
        name="ats-add-application-phase"
        schema={schema}
        defaultValues={{ name: '' }}
        submitConfig={{ type: 'default', hideSubmitButton: true, hideActionBar: true }}
        onSubmit={async (data) => {
          const lastPosition =
            maxBy(hiringStage.applicationPhases, (phase) => phase.position)?.position || 0

          const result = await createAtsApplicationPhase({
            variables: {
              name: data.name,
              atsHiringStageId: hiringStage.id,
              atsJobPostingId: jobId,
              phaseType: getPhaseType(
                hiringStage.name === UNASSIGNED_STAGE ? 'new' : hiringStage.name
              ),
              position: lastPosition + 1,
            },
          })

          const fieldErrors: Record<string, string> = {}
          const handled = handleResult(result, (errors) => {
            Object.assign(fieldErrors, errors)
          })

          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('common.saved_successfully'),
            })
            onClose()
            return { success: true }
          }

          return Object.keys(fieldErrors).length > 0
            ? { success: false, errors: fieldErrors }
            : { success: false }
        }}
      />
    </Modal>
  )
}
